import React from 'react'
import { type Column } from './utils'
import { TranslationKey } from '@/modules/i18n/types'
import { useTar } from '@/modules'
import { MultiPick } from '@duck/ui'

interface UserTableFiltersProps {
  filterText: string
  setFilterText: (text: string) => void
  roleFilter: string[]
  setRoleFilter: (role: string[]) => void
  statusFilter: string[]
  setStatusFilter: (status: string[]) => void
  columns: Column[]
  columnDropdownOpen: boolean
  setColumnDropdownOpen: (open: boolean) => void
  toggleColumnVisibility: (columnKey: string) => void
}

const roleOptions: { value: string, label: TranslationKey }[] = [
  { value: 'admin', label: 'common.admin' },
  { value: 'partner', label: 'common.partner' },
  { value: 'user', label: 'common.user' },
]

const statusOptions: { value: string, label: TranslationKey }[] = [
  { value: 'active-verified', label: 'common.activeVerified' },
  { value: 'active-unverified', label: 'common.activeUnverified' },
  { value: 'inactive', label: 'common.inactive' },
]

export const UsersTableFilters: React.FC<UserTableFiltersProps> = ({
  filterText,
  setFilterText,
  roleFilter,
  setRoleFilter,
  statusFilter,
  setStatusFilter,
  columns,
  columnDropdownOpen,
  setColumnDropdownOpen,
  toggleColumnVisibility,
}) => {
  const { t } = useTar()

  return (
    <div className="space-y-4">
      {/* Primary Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1 min-w-0">
          <label htmlFor="search" className="sr-only">
            {t('users.searchUsers')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              id="search"
              type="text"
              placeholder={t('common.searchByEmailFirstNameLastName')}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="block w-full ps-10 pe-3 py-2 border border-border rounded-md leading-5 bg-card placeholder-secondary text-foreground focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
            />
          </div>
        </div>

        {/* Role Filter */}
        <div className="w-full sm:w-64">
          <label htmlFor="role-filter" className="sr-only">
            {t('common.filterByRole')}
          </label>
          <MultiPick
            options={roleOptions.map(o => ({ value: o.value, label: t(o.label) }))}
            selected={roleFilter}
            onChange={setRoleFilter}
            placeholder={t('common.allRoles')}
            className="py-[6px]"
          />
        </div>

        {/* Status Filter */}
        <div className="w-full sm:w-64">
          <label htmlFor="status-filter" className="sr-only">
            {t('common.filterByStatus')}
          </label>
          <MultiPick
            options={statusOptions.map(o => ({ value: o.value, label: t(o.label) }))}
            selected={statusFilter}
            onChange={setStatusFilter}
            placeholder={t('common.allStatuses')}
            className="py-[6px]"
          />
        </div>

        {/* Columns Filter */}
        <div className="w-full sm:w-64">
          <label className="sr-only">
            {t('common.columns')}
          </label>
          <MultiPick
            options={columns.map(col => ({ value: col.key, label: t(col.label) }))}
            selected={columns.filter(c => c.visible).map(c => c.key)}
            itemsLabel={t('common.items')}
            onChange={(nextSelected) => {
              const selectedSet = new Set(nextSelected)
              columns.forEach(col => {
                const shouldBeVisible = selectedSet.has(col.key)
                if (shouldBeVisible !== col.visible) {
                  toggleColumnVisibility(col.key)
                }
              })
            }}
            placeholder={t('common.columns')}
            className="py-[6px]"
          />
        </div>
      </div>
    </div>
  )
}
